import { tagBackground, tagColor } from '../sharing/tagColors';
import { useTransientNavigationState } from '../navigation/useTransientNavigationState';
import {
  CameraIcon,
  DeleteIcon,
  EditLocationIcon,
  FilterIcon,
  FindReplaceIcon,
  ListPlusIcon,
} from '../../components/ui/icons';
import { createInventoryItemComparator } from '@qkindex/contracts';
import { inventoryKey, recordKey } from '../collection/multiCollectionIdentity';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';

import { BulkActionTransport, useBulkActions } from '../bulk/useBulkActions';
import { CollectionPage } from '../collection/components/CollectionPage';
import { CollectionHeader } from '../collection/components/CollectionHeader';
import { CollectionViewControl } from '../collection/components/CollectionViewControl';
import { CollectionSortControl } from '../collection/components/CollectionSortControl';
import { CollectionSearchField } from '../collection/components/CollectionSearchField';
import { CollectionEmptyState } from '../collection/components/CollectionEmptyState';
import { useCollectionSelection } from '../collection/hooks/useCollectionSelection';
import { CollectionSortOption, ItemSort } from '../collection/types';
import { ItemBulkDialog, ItemBulkModals } from './ItemBulkModals';
import { ItemResults } from './components/ItemResults';
import { ItemFilterPanel } from './components/ItemFilterPanel';
import { CollectionMenuAction } from '../collection/types';

import {
  LocationFilterScope,
  retainAvailableFilterKeys,
  retainAvailableLocationKeys,
  useItemFilters,
} from './useItemFilters';
import type { InventoryTag } from '../sharing/useCollectionTags';
import type { ItemsPageProps } from './ItemsPage';
import type { Item } from '../../domain/item';
import { inventoryTagKey, inventoryTagLabel } from '../sharing/TagFilter';

const itemSortOptions: CollectionSortOption<ItemSort>[] = [
  { value: 'edited-desc', label: 'Recently edited' },
  { value: 'created-desc', label: 'Recently added' },
  { value: 'created-asc', label: 'Oldest added' },
  { value: 'keywords-asc', label: 'Keywords A–Z' },
  { value: 'keywords-desc', label: 'Keywords Z–A' },
  { value: 'location-asc', label: 'Location A–Z' },
  { value: 'location-desc', label: 'Location Z–A' },
  { value: 'quantity-desc', label: 'Quantity high–low' },
  { value: 'quantity-asc', label: 'Quantity low–high' },
];
export type InventoryItemsPageProps = Omit<ItemsPageProps, 'authority'> & {
  identity: { userId: string; collectionId: string; scopeKey: string };
  tagState: { tags: InventoryTag[]; completeCollectionIds: string[] };
  showLocation?: boolean;
  showQuantity?: boolean;
  pageMenuActions?: CollectionMenuAction[];
  onRequestMove?: (items: Item[], onComplete: () => void) => void;
  /** Optional control of the search text, e.g. for suggested searches. */
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
} & (
    | {
        shared?: false;
        authority: ItemsPageProps['authority'];
        bulkTransport?: never;
      }
    | { shared: true; authority?: never; bulkTransport: BulkActionTransport }
  );

export function InventoryItemsPage({
  authority,
  identity,
  tagState,
  bulkTransport,
  shared = false,
  showLocation = true,
  showQuantity = true,
  pageMenuActions = [],
  items,
  locations,
  loading,
  error,
  phoneLayout,
  onCountChange,
  hideCount = false,
  stickyTop = phoneLayout ? 0 : 48,
  shellHeader,
  view,
  onViewChange,
  sort,
  onSortChange,
  cardSize,
  removeMargins = false,
  readOnly = false,
  canDelete = false,
  disableBulkActions = false,
  onRefresh,
  incompleteResults = false,
  inventoryStatus,
  additionsBlocked = false,
  capacityNotice,
  onOpenItem,
  onAddItem,
  onMovedToBin,
  onOpenBin,
  onMoveSelection,
  onRequestMove,
  locationMoveError,
  moveLocations,
  onLoadMoveLocations,
  moveLocationsLoading = false,
  locationFilterScope,
  tagCollections,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
}: InventoryItemsPageProps) {
  const [ownSearchQuery, setOwnSearchQuery] = useState('');
  const searchQuery = controlledSearchQuery ?? ownSearchQuery;
  const setSearchQuery = onSearchQueryChange ?? setOwnSearchQuery;
  const collectionIds = useMemo(
    () =>
      tagCollections?.map((collection) => collection.collectionId) ??
      locationFilterScope?.collectionIds ?? [
        ...new Set(
          [
            identity.collectionId,
            ...items.map((item) => item.collectionId || ''),
            ...locations.map((location) => location.collectionId || ''),
          ].filter(Boolean),
        ),
      ],
    [
      tagCollections,
      locationFilterScope?.collectionIds,
      identity.collectionId,
      items,
      locations,
    ],
  );
  const tags = useMemo(
    () =>
      tagState.tags.map((tag) => ({
        ...tag,
        collectionName:
          collectionIds.length > 1
            ? tagCollections?.find(
                (collection) => collection.collectionId === tag.collectionId,
              )?.name ||
              items.find((item) => item.collectionId === tag.collectionId)
                ?.collectionName ||
              locations.find(
                (location) => location.collectionId === tag.collectionId,
              )?.collectionName
            : undefined,
      })),
    [tagState.tags, tagCollections, items, locations, collectionIds.length],
  );
  const tagFilterScope = useMemo<LocationFilterScope>(
    () => ({
      collectionIds,
      unavailableCollectionIds:
        locationFilterScope?.unavailableCollectionIds || [],
      completeCollectionIds: tagState.completeCollectionIds,
      pending: locationFilterScope?.pending,
    }),
    [
      collectionIds,
      locationFilterScope?.unavailableCollectionIds,
      locationFilterScope?.pending,
      tagState.completeCollectionIds,
    ],
  );
  const [appliedFilters, setAppliedFilters] = useItemFilters(
    shared ? identity.scopeKey : identity.userId,
    !shared,
  );
  const {
    locations: selectedLocationKeys,
    tags: selectedTagKeys,
    description: locationDescriptionSearch,
    locationName: locationNameSearch,
  } = appliedFilters;
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const locationLabels = useRef(new Map<string, string>());
  const tagLabels = useRef(new Map<string, string>());
  useEffect(() => {
    tags.forEach((tag) =>
      tagLabels.current.set(inventoryTagKey(tag), inventoryTagLabel(tag)),
    );
  }, [tags]);
  useEffect(() => {
    setAppliedFilters((previous) => {
      const remaining = retainAvailableFilterKeys(
        previous.tags,
        tags.map(inventoryTagKey),
        tagFilterScope,
      );
      return remaining === previous.tags
        ? previous
        : { ...previous, tags: remaining };
    });
  }, [tags, tagFilterScope, setAppliedFilters]);
  const bulkScopeKey = `items.bulk:${identity.scopeKey}`;
  const [bulkDialog, setBulkDialog, isBulkSessionCurrent] =
    useTransientNavigationState<ItemBulkDialog>(bulkScopeKey, null);
  const itemIds = useMemo(() => items.map(recordKey), [items]);
  const currentItemIds = useRef(new Set(itemIds));
  currentItemIds.current = new Set(itemIds);
  const selection = useCollectionSelection(
    itemIds,
    disableBulkActions && !canDelete && !onMoveSelection && !onRequestMove,
  );
  const deferredSearchQuery = useDeferredValue(searchQuery);
  useEffect(() => {
    if (selection.active) setFilterPanelOpen(false);
  }, [selection.active]);

  const locationsById = useMemo(
    () => new Map(locations.map((location) => [recordKey(location), location])),
    [locations],
  );
  const filterLocations = useMemo(
    () =>
      locations.map((location) => ({
        ...location,
        name: location.collectionName
          ? `${location.collectionName}/${location.name}`
          : location.name,
      })),
    [locations],
  );
  useEffect(() => {
    filterLocations.forEach((location) =>
      locationLabels.current.set(recordKey(location), location.name),
    );
  }, [filterLocations]);
  useEffect(() => {
    setAppliedFilters((previous) => {
      const remaining = retainAvailableLocationKeys(
        previous.locations,
        locations,
        locationFilterScope,
      );
      return remaining === previous.locations
        ? previous
        : { ...previous, locations: remaining };
    });
  }, [locations, locationFilterScope, setAppliedFilters]);
  const locationFilterLabel =
    filterLocations.find(
      (entry) => recordKey(entry) === selectedLocationKeys[0],
    )?.name ||
    locationLabels.current.get(selectedLocationKeys[0]) ||
    'Selected location';
  const selectedTag = tags.find(
    (tag) => inventoryTagKey(tag) === selectedTagKeys[0],
  );
  const tagFilterLabel = selectedTag
    ? inventoryTagLabel(selectedTag)
    : tagLabels.current.get(selectedTagKeys[0]) || 'Selected tag';
  const searchableItems = useMemo(
    () =>
      items.map((item) => {
        const resolvedLocation = item.locationId
          ? locationsById.get(
              recordKey({
                id: item.locationId,
                collectionId: item.collectionId,
              }),
            )
          : undefined;
        const resolvedItem =
          resolvedLocation && resolvedLocation.name !== item.location
            ? { ...item, location: resolvedLocation.name }
            : item;
        const taggedItem = item.tagIds?.length
          ? {
              ...resolvedItem,
              displayTags: tags
                .filter(
                  (tag) =>
                    tag.collectionId ===
                      (item.collectionId || identity.collectionId) &&
                    item.tagIds?.includes(tag.tagId),
                )
                .map((tag) => ({ tagId: tag.tagId, name: tag.name })),
            }
          : resolvedItem;
        const locationRecord = resolvedItem.locationId
          ? locationsById.get(
              recordKey({
                id: resolvedItem.locationId,
                collectionId: resolvedItem.collectionId,
              }),
            )
          : locations.find(
              (location) =>
                location.name === resolvedItem.location &&
                location.collectionId === resolvedItem.collectionId,
            );
        return {
          item: taggedItem,
          sortItem: item,
          keywords: resolvedItem.keywords.toLocaleLowerCase(),
          location: resolvedItem.location.toLocaleLowerCase(),
          description: locationRecord?.description.toLocaleLowerCase() || '',
        };
      }),
    [items, locations, locationsById, tags, identity.collectionId],
  );

  const filteredItems = useMemo(() => {
    const keywordTerms = deferredSearchQuery
      .toLocaleLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const locationNameTerms = locationNameSearch
      .toLocaleLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const descriptionTerms = locationDescriptionSearch
      .toLocaleLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const selectedKeys = new Set(selectedLocationKeys);
    const selectedTags = new Set(selectedTagKeys);

    const compare = createInventoryItemComparator(sort);
    return searchableItems
      .filter(({ item, keywords, location, description }) => {
        const matchesSearch = keywordTerms.every((term) =>
          keywords.includes(term),
        );
        const matchesLocation =
          !selectedKeys.size ||
          (item.locationId
            ? selectedKeys.has(
                recordKey({
                  id: item.locationId,
                  collectionId: item.collectionId,
                }),
              )
            : locations.some(
                (location) =>
                  selectedKeys.has(recordKey(location)) &&
                  item.collectionId === location.collectionId &&
                  item.location === location.name,
              ));
        const matchesLocationName = locationNameTerms.every((term) =>
          location.includes(term),
        );
        const matchesDescription = descriptionTerms.every((term) =>
          description.includes(term),
        );
        const matchesTag =
          !selectedTags.size ||
          item.tagIds?.some((id) =>
            selectedTags.has(
              inventoryKey(item.collectionId || identity.collectionId, id),
            ),
          );
        return (
          matchesSearch &&
          matchesLocation &&
          matchesLocationName &&
          matchesDescription &&
          matchesTag
        );
      })
      .sort((left, right) => compare(left.sortItem, right.sortItem))
      .map(({ item }) => item);
  }, [
    searchableItems,
    locations,
    deferredSearchQuery,
    selectedLocationKeys,
    locationDescriptionSearch,
    locationNameSearch,
    sort,
    selectedTagKeys,
    identity.collectionId,
  ]);

  useEffect(() => {
    onCountChange?.({
      result: filteredItems.length,
      total: items.length,
      incomplete: incompleteResults,
    });
  }, [onCountChange, filteredItems.length, items.length, incompleteResults]);

  const [bulkLocation, setBulkLocation] = useState('');
  const [movingLocation, setMovingLocation] = useState(false);
  const submittingLocation = useRef(false);
  const closeBulk = () => {
    if (!submittingLocation.current) setBulkDialog(null);
  };
  const selectedItems = items.filter((item) =>
    selection.selectedIds.has(recordKey(item)),
  );
  const selectedItemIds = new Set(selectedItems.map((item) => item.id));
  const bulkActions = useBulkActions({
    authority,
    transport: bulkTransport,
    scopeKey: identity.scopeKey,
    items,
    selectedIds: selectedItemIds,
    onComplete: () => {
      if (bulkDialog !== null && !isBulkSessionCurrent()) return;
      setBulkDialog(null);
      selection.clear();
    },
    onMovedToBin,
  });
  const bulkLocations =
    onMoveSelection && moveLocations ? moveLocations : locations;
  const deleteActionIcon = bulkActions.binning ? (
    <CircularProgress size={16} color="inherit" />
  ) : (
    <DeleteIcon fontSize="small" />
  );
  const chosenBulkLocation = bulkLocations.find(
    (entry) =>
      recordKey(entry) === bulkLocation ||
      (!entry.collectionId && entry.name === bulkLocation),
  );
  const moveError = onMoveSelection
    ? locationMoveError?.(selectedItems, chosenBulkLocation)
    : undefined;
  const locationAction = {
    label: 'Move Items',
    icon: <EditLocationIcon fontSize="small" />,
    onClick: () => {
      if (onRequestMove) onRequestMove(selectedItems, selection.clear);
      else {
        setBulkDialog('location');
        onLoadMoveLocations?.();
      }
    },
    disabled: !selection.selectedIds.size,
  };
  const confirmBulkLocation = async () => {
    if (
      submittingLocation.current ||
      !onMoveSelection ||
      !chosenBulkLocation ||
      moveError
    )
      return;
    submittingLocation.current = true;
    setMovingLocation(true);
    try {
      const result = await onMoveSelection(selectedItems, chosenBulkLocation);
      if (!isBulkSessionCurrent()) return;
      const remaining = [...result.failed, ...result.unsubmitted].filter(
        (key) => currentItemIds.current.has(key),
      );
      selection.clear();
      if (remaining.length) selection.select(remaining);
      setBulkLocation('');
      setBulkDialog(null);
    } finally {
      submittingLocation.current = false;
      setMovingLocation(false);
    }
  };
  const clearFilters = () =>
    setAppliedFilters({
      locations: [],
      tags: [],
      description: '',
      locationName: '',
    });
  const appliedFilterCount =
    Number(selectedLocationKeys.length > 0) +
    Number(selectedTagKeys.length > 0) +
    Number(Boolean(locationDescriptionSearch)) +
    Number(Boolean(locationNameSearch.trim()));

  const itemResultsKey = JSON.stringify([
    deferredSearchQuery,
    selectedLocationKeys,
    selectedTagKeys,
    locationDescriptionSearch,
    locationNameSearch,
    sort,
    view,
    cardSize,
  ]);

  const toolbar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <CollectionSearchField
        label="Search items"
        placeholder="Search items…"
        clearLabel="Clear item search"
        value={searchQuery}
        onChange={setSearchQuery}
      />
      {appliedFilterCount > 0 && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ overflowX: 'auto', py: 0.5 }}
        >
          {selectedLocationKeys.length > 0 && (
            <Chip
              label={
                selectedLocationKeys.length === 1
                  ? `Location: ${locationFilterLabel}`
                  : `Locations: ${selectedLocationKeys.length} selected`
              }
              onDelete={() =>
                setAppliedFilters((previous) => ({
                  ...previous,
                  locations: [],
                }))
              }
            />
          )}
          {locationNameSearch.trim() && (
            <Chip
              label={`Location name contains: ${locationNameSearch}`}
              onDelete={() =>
                setAppliedFilters((previous) => ({
                  ...previous,
                  locationName: '',
                }))
              }
            />
          )}
          {locationDescriptionSearch && (
            <Chip
              label={`Description: ${locationDescriptionSearch}`}
              onDelete={() =>
                setAppliedFilters((previous) => ({
                  ...previous,
                  description: '',
                }))
              }
            />
          )}
          {selectedTagKeys.length > 0 && (
            <Chip
              sx={
                selectedTagKeys.length === 1 && selectedTag
                  ? {
                      '&.MuiChip-filled.MuiChip-colorDefault': {
                        color: (theme) =>
                          tagColor(selectedTag.tagId, theme.palette.mode),
                        bgcolor: (theme) =>
                          tagBackground(selectedTag.tagId, theme.palette.mode),
                        fontStyle: 'italic',
                        borderRadius: '999px',
                      },
                      '& .MuiChip-deleteIcon': { color: 'inherit' },
                    }
                  : undefined
              }
              label={
                selectedTagKeys.length === 1
                  ? `Tag: ${tagFilterLabel}`
                  : `Tags: ${selectedTagKeys.length} selected`
              }
              onDelete={() =>
                setAppliedFilters((previous) => ({ ...previous, tags: [] }))
              }
            />
          )}
          <Button onClick={clearFilters}>Clear all</Button>
        </Stack>
      )}
    </Box>
  );

  const header = (
    <CollectionHeader
      hideCount={hideCount}
      onRefresh={onRefresh}
      incomplete={incompleteResults}
      inventoryStatus={inventoryStatus}
      noun="item"
      resultCount={filteredItems.length}
      totalCount={items.length}
      visibleIds={filteredItems.map(recordKey)}
      selection={selection}
      disableSelection={
        disableBulkActions && !canDelete && !onMoveSelection && !onRequestMove
      }
      phoneLayout={phoneLayout}
      filterControl={
        showLocation && (
          <Badge
            color="primary"
            badgeContent={appliedFilterCount}
            invisible={!appliedFilterCount}
            slotProps={{ badge: { style: { top: 4 } } }}
          >
            {phoneLayout ? (
              <IconButton
                size="small"
                aria-label={
                  appliedFilterCount
                    ? `Filters, ${appliedFilterCount} applied`
                    : 'Filters'
                }
                aria-expanded={filterPanelOpen}
                onClick={() => setFilterPanelOpen((open) => !open)}
                sx={{ flexShrink: 0 }}
              >
                <FilterIcon />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterIcon />}
                aria-label={
                  appliedFilterCount
                    ? `Filters, ${appliedFilterCount} applied`
                    : 'Filters'
                }
                aria-expanded={filterPanelOpen}
                onClick={() => setFilterPanelOpen((open) => !open)}
                sx={{ flexShrink: 0 }}
              >
                Filters
              </Button>
            )}
          </Badge>
        )
      }
      sortControl={
        <CollectionSortControl
          noun="item"
          value={sort}
          options={itemSortOptions.filter(
            (option) =>
              (showLocation || !option.value.startsWith('location-')) &&
              (showQuantity || !option.value.startsWith('quantity-')) &&
              (!shared || !/^(created|edited)-/.test(option.value)),
          )}
          compact
          onChange={onSortChange}
        />
      }
      viewControl={
        <CollectionViewControl value={view} onChange={onViewChange} />
      }
      expandedPanel={
        <ItemFilterPanel
          key={identity.scopeKey}
          showTags={!shared}
          showLocationDescription={!shared}
          locationFilterScope={locationFilterScope}
          tagFilterScope={tagFilterScope}
          tags={tags}
          open={filterPanelOpen}
          phoneLayout={phoneLayout}
          applied={appliedFilters}
          locations={locations}
          onClose={() => setFilterPanelOpen(false)}
          onApply={(filters) => {
            setAppliedFilters(filters);
            setFilterPanelOpen(false);
          }}
        />
      }
      bulkActions={
        readOnly || disableBulkActions
          ? [
              ...(onMoveSelection || onRequestMove ? [locationAction] : []),
              ...(canDelete
                ? [
                    {
                      label: bulkActions.binning ? 'Deleting…' : 'Delete Items',
                      icon: deleteActionIcon,
                      onClick: bulkActions.moveToBin,
                      disabled:
                        bulkActions.binning || !selection.selectedIds.size,
                      color: 'error' as const,
                    },
                  ]
                : []),
            ]
          : [
              {
                label: 'Append Keywords',
                icon: <ListPlusIcon fontSize="small" />,
                onClick: () => setBulkDialog('keywords'),
                disabled: !selection.selectedIds.size,
              },
              {
                label: 'Find & Replace',
                icon: <FindReplaceIcon fontSize="small" />,
                onClick: () => setBulkDialog('replace'),
                disabled: !selection.selectedIds.size,
              },
              ...(onMoveSelection || onRequestMove ? [locationAction] : []),
              {
                label: 'Edit Image',
                icon: <CameraIcon fontSize="small" />,
                onClick: () => setBulkDialog('image'),
                disabled: !selection.selectedIds.size,
              },
              {
                label: bulkActions.binning ? 'Deleting…' : 'Delete Items',
                icon: deleteActionIcon,
                onClick: bulkActions.moveToBin,
                disabled: bulkActions.binning || !selection.selectedIds.size,
                color: 'error',
              },
            ]
      }
      pageMenuActions={
        [
          ...pageMenuActions,
          ...(onOpenBin
            ? [
                {
                  label: 'Bin',
                  icon: <DeleteIcon fontSize="small" />,
                  onClick: onOpenBin,
                },
              ]
            : []),
        ] as CollectionMenuAction[]
      }
    />
  );

  const statusContent = capacityNotice ? (
    <Alert severity="info" sx={{ mx: removeMargins ? 0 : 2, mt: 1 }}>
      {capacityNotice}
    </Alert>
  ) : undefined;

  return (
    <>
      <CollectionPage
        title="Items"
        fullWidth={removeMargins}
        stickyTop={stickyTop}
        shellHeader={shellHeader}
        toolbar={toolbar}
        header={header}
        beforeContent={statusContent}
        loading={loading}
        loadingIndicatorInHeader={Boolean(inventoryStatus)}
        error={error}
        empty={
          <CollectionEmptyState
            title={
              incompleteResults
                ? 'No matches in loaded results yet'
                : items.length
                  ? 'No matching items'
                  : 'No items yet'
            }
            description={
              incompleteResults
                ? 'More results are available. Load more or search to check the remaining items.'
                : items.length
                  ? 'Try changing your search or filters.'
                  : shared && readOnly
                    ? 'No items are currently shared.'
                    : additionsBlocked
                      ? 'Item creation is currently unavailable, but existing content remains accessible.'
                      : 'Use the add button to create your first item.'
            }
          />
        }
        addLabel="Add item"
        onAdd={readOnly || additionsBlocked ? undefined : onAddItem}
        phoneLayout={phoneLayout}
      >
        {filteredItems.length ? (
          <ItemResults
            key={itemResultsKey}
            items={filteredItems}
            view={view}
            selection={selection}
            onOpen={onOpenItem}
            cardSize={cardSize}
            showLocation={showLocation}
            showQuantity={showQuantity}
          />
        ) : null}
      </CollectionPage>
      <ItemBulkModals
        scopeKey={bulkScopeKey}
        open={
          bulkDialog === 'location'
            ? onMoveSelection
              ? bulkDialog
              : null
            : readOnly || disableBulkActions
              ? null
              : bulkDialog
        }
        onClose={closeBulk}
        location={bulkLocation}
        locations={bulkLocations}
        onLocationChange={setBulkLocation}
        onLocationConfirm={() => {
          void confirmBulkLocation();
        }}
        locationError={movingLocation ? undefined : moveError}
        selectedCount={selection.selectedIds.size}
        locationBusy={movingLocation}
        locationsLoading={moveLocationsLoading}
        onKeywordsConfirm={bulkActions.appendKeywords}
        onReplaceConfirm={bulkActions.replaceKeywords}
        onImageCapture={bulkActions.applyImage}
        imageProcessing={bulkActions.processingImage}
        imageProgress={bulkActions.imageProgress}
      />
    </>
  );
}
